import { useMemo } from "react";

import { InputVariablesCellRenderer } from "./cells/InputVariablesCell";
import { InputTextCellRenderer } from "./cells/InputTextCell";
import { PromptNameCellRenderer } from "./cells/PromptNameCell";
import { ResponsePreviewCellRenderer } from "./cells/ResponsePreviewCell";
import { ScoreCellRenderer } from "./cells/ScoreCell";
import { StatusCellRenderer } from "./cells/StatusCell";
import { TagsCellRenderer } from "./cells/TagsCell";
import { SignalsCellRenderer } from "./cells/SignalsCell";
import { HydrationPendingCell, isRequestRowHydrating } from "./cells/HydrationPendingCell";
import { formatCountCell, formatLatency } from "./columnFormatters";
import type { RequestRow } from "./requestLogGridTypes";
import { SelectionCheckboxCellRenderer, SelectionHeaderRenderer } from "./selectionCells";

import { formatTimestampInZone } from "@/utils/timestamp";
import { createActionsColumn } from "@/components/ui/ag-grid-table/createActionsColumn";
import type { ContextMenuItems } from "@/components/ui/ContextMenu/types";
import { TableColumn } from "@/components/ui/TableV2/types";

export type { RequestRow } from "./requestLogGridTypes";

const NUMERIC_CELL_CLASS = "text-right tabular-nums";
const NUMERIC_CELL_CONTENT_CLASS = "justify-end text-right";
const NUMERIC_CELL_CONTENT_STYLE = { justifyContent: "end" };

const renderCountCell = ({ data, value }: { data?: RequestRow; value: number | string | null }) => {
  if (isRequestRowHydrating(data) && (value === null || value === undefined)) {
    return <HydrationPendingCell />;
  }
  return formatCountCell(value);
};

const renderStartTimeCell = ({
  data,
  value,
  timezone,
}: {
  data?: RequestRow;
  value: string;
  timezone?: string;
}) => {
  if (isRequestRowHydrating(data) && !value) {
    return <HydrationPendingCell />;
  }
  if (!value) {
    return "";
  }
  return formatTimestampInZone(value, timezone, { includeSeconds: true, showRelative: false });
};

const renderTokenCell = ({ data, value }: { data?: RequestRow; value: number | undefined }) => {
  if (isRequestRowHydrating(data) && (value === null || value === undefined)) {
    return <HydrationPendingCell />;
  }
  if (!value) {
    return "-";
  }
  return value.toLocaleString();
};

export interface RequestColumnsOptions {
  /** Entity analytics pages already scope to one prompt — hide the redundant name column. */
  hidePromptName?: boolean;
  /** Prefer Start Time near the left edge on entity analytics tables. */
  prioritizeStartTime?: boolean;
  selectionLimit?: 1;
}

const moveColumnAfterSelect = <T extends { name: string }>(
  columns: T[],
  columnName: string,
): T[] => {
  const column = columns.find(item => item.name === columnName);
  if (!column) {
    return columns;
  }
  const withoutColumn = columns.filter(item => item.name !== columnName);
  const selectIndex = withoutColumn.findIndex(item => item.name === "select");
  const insertAt = selectIndex >= 0 ? selectIndex + 1 : 0;
  return [...withoutColumn.slice(0, insertAt), column, ...withoutColumn.slice(insertAt)];
};

export const useRequestColumns = (
  timezone?: string,
  contextMenuItems?: ContextMenuItems<RequestRow>[],
  options?: RequestColumnsOptions,
): TableColumn<RequestRow>[] =>
  useMemo(() => {
    const hidePromptName = options?.hidePromptName ?? false;
    const prioritizeStartTime = options?.prioritizeStartTime ?? false;

    const columns: TableColumn<RequestRow>[] = [
      {
        id: -1,
        name: "select",
        header: "",
        width: 44,
        minWidth: 44,
        maxWidth: 44,
        sortable: false,
        resizable: false,
        suppressMovable: true,
        suppressRowLink: true,
        pinned: "left",
        cellRenderer: SelectionCheckboxCellRenderer,
        cellRendererParams: { selectionLimit: options?.selectionLimit },
        headerComponent: SelectionHeaderRenderer,
        headerComponentParams: { selectionLimit: options?.selectionLimit },
        cellClass:
          "!justify-center [&_.ag-cell-wrapper]:!justify-center [&_.ag-cell-value]:!justify-center",
        headerClass:
          "[&_.ag-cell-label-container]:!justify-center [&_.ag-header-cell-label]:!justify-center [&_.ag-header-cell-comp-wrapper]:!justify-center",
      },
      {
        id: 0,
        name: "engine",
        header: "Model Name",
        field: "engine",
        fitContent: true,
        sortable: false,
        cellRenderer: ({ data }: { data?: RequestRow }) =>
          isRequestRowHydrating(data) && !data?.engine ? (
            <HydrationPendingCell />
          ) : (
            (data?.engine ?? "")
          ),
      },
      {
        id: 1,
        name: "status",
        header: "Status",
        field: "status",
        cellRenderer: StatusCellRenderer,
      },
      {
        id: 2,
        name: "prompt_name",
        header: "Prompt Name",
        field: "prompt_name",
        cellRenderer: PromptNameCellRenderer,
        fitContent: true,
        sortable: false,
      },
      {
        id: 14,
        name: "input_text",
        header: "Input Text",
        field: "input_text",
        cellRenderer: InputTextCellRenderer,
        sortable: false,
        width: 364,
        minWidth: 364,
        wrapText: true,
        cellContentClassName: "min-w-0 items-center justify-start overflow-hidden",
        cellClass:
          "!items-center [&_.ag-cell-value]:!items-center [&_.ag-cell-value]:!justify-start [&_.ag-cell]:!overflow-hidden",
        autoHeight: false,
      },
      {
        id: 3,
        name: "request_response_preview",
        header: "Response Preview",
        field: "request_response_preview",
        cellRenderer: ResponsePreviewCellRenderer,
        sortable: false,
        width: 364,
        minWidth: 364,
        wrapText: true,
        cellContentClassName: "min-w-0 items-center justify-start overflow-hidden",
        cellClass:
          "!items-center [&_.ag-cell-value]:!items-center [&_.ag-cell-value]:!justify-start [&_.ag-cell]:!overflow-hidden",
        autoHeight: false,
      },
      {
        id: 4,
        name: "turn_count",
        header: "# of Turns",
        field: "turn_count",
        sortable: true,
        width: 104,
        minWidth: 96,
        maxWidth: 140,
        cellClass: NUMERIC_CELL_CLASS,
        cellContentClassName: NUMERIC_CELL_CONTENT_CLASS,
        cellContentStyle: NUMERIC_CELL_CONTENT_STYLE,
        cellRenderer: renderCountCell,
      },
      {
        id: 5,
        name: "tool_call_count",
        header: "# of Tool Calls",
        field: "tool_call_count",
        sortable: true,
        width: 132,
        minWidth: 118,
        maxWidth: 180,
        cellClass: NUMERIC_CELL_CLASS,
        cellContentClassName: NUMERIC_CELL_CONTENT_CLASS,
        cellContentStyle: NUMERIC_CELL_CONTENT_STYLE,
        cellRenderer: renderCountCell,
      },
      {
        id: 6,
        name: "prompt_input_variables",
        header: "Input Variables",
        field: "prompt_input_variables_preview",
        cellRenderer: InputVariablesCellRenderer,
        width: 246,
        minWidth: 246,
        sortable: false,
      },
      {
        id: 7,
        name: "request_start_time",
        header: "Start Time",
        field: "request_start_time",
        width: 246,
        minWidth: 246,
        sortable: true,
        fitContent: true,
        cellRenderer: ({ data, value }: { data?: RequestRow; value: string }) =>
          renderStartTimeCell({ data, value, timezone }),
      },
      {
        id: 8,
        name: "score",
        header: "Score",
        field: "score",
        cellRenderer: ScoreCellRenderer,
        sortable: false,
        cellClass: NUMERIC_CELL_CLASS,
        cellContentClassName: NUMERIC_CELL_CONTENT_CLASS,
        cellContentStyle: NUMERIC_CELL_CONTENT_STYLE,
      },
      {
        id: 9,
        name: "price",
        header: "Cost",
        field: "price",
        sortable: true,
        cellClass: NUMERIC_CELL_CLASS,
        cellContentClassName: NUMERIC_CELL_CONTENT_CLASS,
        cellContentStyle: NUMERIC_CELL_CONTENT_STYLE,
        cellRenderer: ({
          data,
          value,
        }: {
          data?: RequestRow;
          value: number | string | undefined;
        }) => {
          if (isRequestRowHydrating(data) && (value === null || value === undefined)) {
            return <HydrationPendingCell />;
          }
          if (!value) {
            return "";
          }
          const num = typeof value === "number" ? value : parseFloat(value) || 0;
          return `$${num.toFixed(6)}`;
        },
      },
      {
        id: 10,
        name: "input_tokens",
        header: "Input Tokens",
        field: "input_tokens",
        sortable: true,
        cellClass: NUMERIC_CELL_CLASS,
        cellContentClassName: NUMERIC_CELL_CONTENT_CLASS,
        cellContentStyle: NUMERIC_CELL_CONTENT_STYLE,
        cellRenderer: renderTokenCell,
      },
      {
        id: 11,
        name: "output_tokens",
        header: "Output Tokens",
        field: "output_tokens",
        sortable: true,
        cellClass: NUMERIC_CELL_CLASS,
        cellContentClassName: NUMERIC_CELL_CONTENT_CLASS,
        cellContentStyle: NUMERIC_CELL_CONTENT_STYLE,
        cellRenderer: renderTokenCell,
      },
      {
        id: 12,
        name: "latency",
        header: "Latency",
        field: "latency",
        sortable: true,
        cellClass: NUMERIC_CELL_CLASS,
        cellContentClassName: NUMERIC_CELL_CONTENT_CLASS,
        cellContentStyle: NUMERIC_CELL_CONTENT_STYLE,
        cellRenderer: ({ data, value }: { data?: RequestRow; value: number | string | null }) => {
          if (isRequestRowHydrating(data) && (value === null || value === undefined)) {
            return <HydrationPendingCell />;
          }
          return formatLatency(value, data);
        },
      },
      {
        id: 13,
        name: "tags_array",
        header: "Tags",
        field: "tags_array",
        cellRenderer: TagsCellRenderer,
        width: 326,
        minWidth: 168,
        sortable: false,
      },
      {
        id: 16,
        name: "signals",
        header: "Signals",
        field: "metadata",
        cellRenderer: SignalsCellRenderer,
        width: 326,
        minWidth: 168,
        sortable: false,
      },
      {
        ...createActionsColumn<RequestRow>(contextMenuItems, 15),
        suppressRowLink: true,
      },
    ];

    const withoutPromptName = hidePromptName
      ? columns.filter(column => column.name !== "prompt_name")
      : columns;

    return prioritizeStartTime
      ? moveColumnAfterSelect(withoutPromptName, "request_start_time")
      : withoutPromptName;
  }, [
    contextMenuItems,
    options?.hidePromptName,
    options?.prioritizeStartTime,
    options?.selectionLimit,
    timezone,
  ]);
